import { useState, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useMicro } from '../../hooks/useMicro.ts'
import { userInfoData } from '../../hooks/useAuth'
import { Modal, Icon } from '../common/index.ts'
import { style, overrideTopbarModalStyles } from './style.ts'
import { style as modalCommonStyles } from '../common/Modal/style.ts'
import { emitEvent } from '../../utils/index.ts'

type ModalMicroProps = {
  root?: HTMLElement | null,
  microId: string,
  baseUrl: string,
  checkToken?: () => void,
  data?: any,
  userInfo?: userInfoData
  onChange?: (microId: string, baseUrl: string) => void,
}

type modalDimensions = {
  width?: string | number,
  height?: string | number,
  maxWidth?: string | number,
  maxHeight?: string | number
}

export const ModalMicro = ({
  root,
  microId = '',
  baseUrl = '',
  data,
  onChange,
  checkToken,
  userInfo
} : ModalMicroProps) => {

  const [isActive, setActive] = useState<boolean>(false)
  const [internalMicroId, setInternalMicroId] = useState<string>('')
  const [internalBaseUrl, setInternalBaseUrl] = useState<string>('')
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setInternalMicroId(microId)
    setInternalBaseUrl(baseUrl)
  }, [microId, baseUrl])

  // Modal props

  const defaultModalDimensions: modalDimensions = { width: 400 }
  const [modalDimensions, setModalDimensions] = useState<modalDimensions>(defaultModalDimensions)
  const [showCloseIcon, setShowCloseIcon] = useState<boolean>(true)

  useEffect(() => {
    const showX = data?.showCloseIcon == null ? true : data?.showCloseIcon
    setModalDimensions(defaultModalDimensions)
    setShowCloseIcon(showX)
  }, [data?.showCloseIcon, internalMicroId])

  function closeModal () {
    emitEvent(root, `close-${internalMicroId}`)
    setInternalMicroId('')
    setInternalBaseUrl('')
  }

  // Load Micro

  const containerId = `topbar-micro-${microId}`
  const microUtils = useMemo(() => ({
    data,
    user: userInfo,
    language: userInfo?.language,
    showCloseIcon: setShowCloseIcon,
    closeModal,
    checkToken,
    setModal (newDimensions: modalDimensions) {
      setModalDimensions({ ...modalDimensions, ...newDimensions })
    }
  }), [data, userInfo, checkToken])

  const { loading, error, app } = useMicro({
    root: containerRef.current,
    microId: internalMicroId,
    baseUrl: internalBaseUrl,
    data: microUtils
  })

  useEffect(() => {
    onChange && onChange(internalMicroId, internalBaseUrl)
  }, [internalMicroId, internalBaseUrl])

  useEffect(() => {
    if (loading || !app) return
    setActive(!!internalMicroId)
  }, [loading, app, internalMicroId])

  useEffect(() => {
    if (error) {
      closeModal()
    }
  }, [error])

  return createPortal(
    <div>
      <style>{modalCommonStyles}{overrideTopbarModalStyles}</style>
      <Modal
        active={isActive}
        {...modalDimensions}
        eager={true}
      >
        <style>{style}</style>
        {showCloseIcon && <Icon
          name="icon-close"
          className="icon-close"
          size="24"
          onClick={closeModal}
        />}
        <div
          style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}
          id={containerId}
          ref={containerRef}
        />
      </Modal>
    </div>
    ,
    document.body
  )
}